import { useContent } from '../cms/content.js';

// Starting-kit titles that launch a full-screen interactive map instead of the
// brief funnel. Anything not listed falls through to the #brief anchor.
const KIT_ROUTES = {
  'Hidden Gem Map': '#/hidden-gem-map',
  'Neighborhood & I': '#/neighborhood-explorer',
};

export default function StartingKits() {
  const cms = useContent();
  const section = cms.startingKits;
  const kits = (cms.fee.packages || []).filter((pkg) => pkg.isStartingKit);

  if (!section || kits.length === 0) return null;

  return (
    <section
      id="starting-kits"
      style={{
        position: 'relative',
        zIndex: 10,
        overflow: 'hidden',
        background:
          'radial-gradient(100% 80% at 50% 0%, rgba(201,168,106,0.07), transparent 58%), #0a0a0b',
        borderTop: '1px solid rgba(201,168,106,0.14)',
        padding: '140px clamp(18px, 3vw, 32px)',
      }}
    >
      <div style={{ maxWidth: '1320px', margin: '0 auto' }}>
        <div data-reveal="" style={{ textAlign: 'center', marginBottom: '72px' }}>
          <div
            style={{
              font: "500 12px/1 'JetBrains Mono',monospace",
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: '#c9a86a',
              marginBottom: '24px',
            }}
          >
            {section.eyebrow}
          </div>
          <h2
            style={{
              margin: '0 auto 24px',
              fontFamily: "'Cormorant',serif",
              fontWeight: 500,
              fontSize: 'clamp(44px, 6vw, 76px)',
              lineHeight: 0.98,
              color: '#f5f0e6',
            }}
          >
            {section.headingLine1}
            <br />
            <span style={{ fontStyle: 'italic', fontWeight: 300, color: '#e8c47e' }}>
              {section.headingLine2}
            </span>
          </h2>
          <p
            style={{
              margin: '0 auto',
              maxWidth: '780px',
              fontSize: '18px',
              lineHeight: 1.72,
              fontWeight: 300,
              color: '#9b958a',
            }}
          >
            {section.intro}
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
            gap: '26px',
            alignItems: 'stretch',
          }}
        >
          {kits.map((kit, index) => (
            <article
              key={kit.title}
              data-reveal=""
              className="kny-card kny-kit"
              style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '680px',
                padding: 'clamp(34px, 3.5vw, 52px) clamp(26px, 3vw, 42px)',
                border: '1px solid rgba(201,168,106,0.26)',
                borderRadius: '4px',
                background:
                  'linear-gradient(155deg, rgba(18,19,22,0.98) 0%, rgba(12,13,16,0.98) 62%, rgba(10,10,11,0.98) 100%)',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  paddingBottom: '28px',
                  borderBottom: '1px solid rgba(201,168,106,0.18)',
                  marginBottom: '28px',
                }}
              >
                <div
                  style={{
                    font: "500 10px/1 'JetBrains Mono',monospace",
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#6f6a60',
                    marginBottom: '14px',
                  }}
                >
                  Starting Kit {String(index + 1).padStart(2, '0')}
                </div>
                <h3
                  style={{
                    margin: '0 0 10px',
                    fontFamily: "'Cormorant',serif",
                    fontWeight: 500,
                    fontSize: 'clamp(30px, 2.9vw, 40px)',
                    lineHeight: 1.03,
                    color: '#f5f0e6',
                  }}
                >
                  {kit.title}
                </h3>
                <div
                  style={{
                    font: "500 10px/1.45 'JetBrains Mono',monospace",
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#c9a86a',
                  }}
                >
                  {kit.subtitle}
                </div>

                {/* Dedicated price band — same position on every card. */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '10px',
                    marginTop: '26px',
                    paddingTop: '22px',
                    borderTop: '1px solid rgba(201,168,106,0.14)',
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Bodoni Moda',serif",
                      fontWeight: 600,
                      fontSize: 'clamp(42px, 4.6vw, 58px)',
                      lineHeight: 0.9,
                      color: '#e8c47e',
                      fontVariantNumeric: 'lining-nums tabular-nums',
                    }}
                  >
                    {kit.price}
                  </span>
                  {kit.interval && (
                    <span
                      style={{
                        font: "500 9px/1 'JetBrains Mono',monospace",
                        letterSpacing: '0.16em',
                        textTransform: 'uppercase',
                        color: '#6f6a60',
                      }}
                    >
                      {kit.interval.trim()}
                    </span>
                  )}
                </div>
              </div>

              <p
                style={{
                  margin: '0 0 32px',
                  fontSize: '15.5px',
                  lineHeight: 1.72,
                  fontWeight: 300,
                  color: '#b2ab9f',
                }}
              >
                {kit.longDescription || kit.description}
              </p>

              <div style={{ marginTop: 'auto' }}>
                <div
                  style={{
                    marginBottom: '18px',
                    font: "500 10px/1 'JetBrains Mono',monospace",
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: '#6f6a60',
                  }}
                >
                  What your kit covers
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    marginBottom: '34px',
                  }}
                >
                  {(kit.included || []).map((item) => (
                    <div
                      key={item}
                      style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          color: '#c9a86a',
                          font: "500 11px/1.5 'JetBrains Mono',monospace",
                        }}
                      >
                        —
                      </span>
                      <span
                        style={{
                          fontSize: '13.5px',
                          lineHeight: 1.5,
                          fontWeight: 300,
                          color: '#cfc9bc',
                        }}
                      >
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <a
                  href={KIT_ROUTES[kit.title] || '#brief'}
                  aria-label={
                    KIT_ROUTES[kit.title]
                      ? `Open ${kit.title}`
                      : `${kit.ctaLabel || 'Request access'} — ${kit.title}`
                  }
                  className="kny-cta kny-cta-pulse"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '19px 22px',
                    borderRadius: '2px',
                    background: '#c9a86a',
                    color: '#0a0a0b',
                    textAlign: 'center',
                    textDecoration: 'none',
                    font: "600 11px/1 'JetBrains Mono',monospace",
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                  }}
                >
                  {kit.ctaLabel || 'Request access →'}
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
